import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Package,
  MapPin,
  Settings,
  AlertCircle,
  Loader2,
  X,
  Printer,
  CheckCircle,
  Building,
  Box,
  Tag,
  Plus,
  Minus,
  Eye,
  Download,
  Globe,
  DollarSign,
  Weight,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Order, ShipFromAddress, PackageDimensions, MiscItem } from '../types';
import { formatCurrency } from '../lib/utils';
import {
  getShipFromAddresses,
  getPackageDimensions,
  getMiscItems,
  validateCustomsData,
  getShippingRates,
  generateShippingLabel,
  saveLabelToOrder,
  EasyPostValidationResponse,
  EasyPostRateResponse,
  EasyPostLabelResponse,
} from '../services/easypostService';

interface EasyPostLabelModalProps {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface SelectedMiscItem {
  item: MiscItem;
  quantity: number;
}

export function EasyPostLabelModal({ order, isOpen, onClose, onSuccess }: EasyPostLabelModalProps) {
  // Configuration state
  const [shipFromAddresses, setShipFromAddresses] = useState<ShipFromAddress[]>([]);
  const [packageDimensions, setPackageDimensions] = useState<PackageDimensions[]>([]);
  const [miscItems, setMiscItems] = useState<MiscItem[]>([]);
  
  // Selection state
  const [selectedShipFromAddress, setSelectedShipFromAddress] = useState('');
  const [selectedPackageDimensions, setSelectedPackageDimensions] = useState('');
  const [selectedMiscItems, setSelectedMiscItems] = useState<SelectedMiscItem[]>([]);
  const [contentsType, setContentsType] = useState<'merchandise' | 'gift' | 'documents' | 'sample' | 'returned_goods' | 'other'>('merchandise');
  
  // Process state
  const [currentStep, setCurrentStep] = useState<'configure' | 'validate' | 'rates' | 'generate' | 'complete'>('configure');
  const [isLoading, setIsLoading] = useState(false);
  const [validationResult, setValidationResult] = useState<EasyPostValidationResponse | null>(null);
  const [availableRates, setAvailableRates] = useState<EasyPostRateResponse[]>([]);
  const [selectedRate, setSelectedRate] = useState<EasyPostRateResponse | null>(null);
  const [generatedLabel, setGeneratedLabel] = useState<EasyPostLabelResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load configuration data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadConfigurationData();
    } else {
      // Reset state when modal closes
      resetModalState();
    }
  }, [isOpen]);

  const resetModalState = () => {
    setSelectedShipFromAddress('');
    setSelectedPackageDimensions('');
    setSelectedMiscItems([]);
    setContentsType('merchandise');
    setCurrentStep('configure');
    setIsLoading(false);
    setValidationResult(null);
    setAvailableRates([]);
    setSelectedRate(null);
    setGeneratedLabel(null);
    setError(null);
  };

  const loadConfigurationData = async () => {
    try {
      setIsLoading(true);
      const [addresses, dimensions, items] = await Promise.all([
        getShipFromAddresses(),
        getPackageDimensions(),
        getMiscItems(),
      ]);
      
      setShipFromAddresses(addresses);
      setPackageDimensions(dimensions);
      setMiscItems(items);
      
      // Auto-select defaults if available
      const defaultAddress = addresses.find(addr => addr.isDefault) || addresses[0];
      const defaultDimensions = dimensions.find(dim => dim.isDefault) || dimensions[0];
      
      if (defaultAddress) setSelectedShipFromAddress(defaultAddress.id || '');
      if (defaultDimensions) setSelectedPackageDimensions(defaultDimensions.id || '');
      
    } catch (error) {
      console.error('Error loading configuration data:', error);
      setError('Failed to load shipping configuration. Please check your settings.');
    } finally {
      setIsLoading(false);
    }
  };

  const addMiscItem = (item: MiscItem) => {
    const existing = selectedMiscItems.find(selected => selected.item.id === item.id);
    if (existing) {
      setSelectedMiscItems(prev => 
        prev.map(selected => 
          selected.item.id === item.id 
            ? { ...selected, quantity: selected.quantity + 1 }
            : selected
        )
      );
    } else {
      setSelectedMiscItems(prev => [...prev, { item, quantity: 1 }]);
    }
  };

  const removeMiscItem = (itemId: string) => {
    setSelectedMiscItems(prev => prev.filter(selected => selected.item.id !== itemId));
  };

  const updateMiscItemQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeMiscItem(itemId);
      return;
    }
    setSelectedMiscItems(prev => 
      prev.map(selected => 
        selected.item.id === itemId 
          ? { ...selected, quantity }
          : selected
      )
    );
  };

  const validateConfiguration = async () => {
    if (!order || !selectedShipFromAddress || !selectedPackageDimensions) {
      setError('Please select ship-from address and package dimensions');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const validation = await validateCustomsData(
        order.id, 
        selectedMiscItems.map(({ item, quantity }) => ({ itemId: item.id!, quantity }))
      );
      
      setValidationResult(validation);
      
      if (validation.isValid) {
        setCurrentStep('rates');
        await loadShippingRates();
      } else {
        setError(`Missing customs data: ${validation.missingFields.join(', ')}`);
      }
    } catch (error) {
      console.error('Validation failed:', error);
      setError('Failed to validate customs data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadShippingRates = async () => {
    if (!order || !selectedShipFromAddress || !selectedPackageDimensions) return;

    try {
      setIsLoading(true);
      const rates = await getShippingRates(
        order.id,
        selectedShipFromAddress,
        selectedPackageDimensions,
        selectedMiscItems.map(({ item, quantity }) => ({ itemId: item.id!, quantity }))
      );
      
      setAvailableRates(rates);
      // Auto-select the cheapest rate
      if (rates.length > 0) {
        const cheapestRate = rates.reduce((prev, current) => 
          parseFloat(prev.rate) < parseFloat(current.rate) ? prev : current
        );
        setSelectedRate(cheapestRate);
      }
    } catch (error) {
      console.error('Failed to load shipping rates:', error);
      setError('Failed to load shipping rates. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const generateLabel = async () => {
    if (!order || !selectedShipFromAddress || !selectedPackageDimensions) return;

    try {
      setIsLoading(true);
      setCurrentStep('generate');
      
      const labelData = await generateShippingLabel(
        order.id,
        selectedShipFromAddress,
        selectedPackageDimensions,
        selectedMiscItems.map(({ item, quantity }) => ({ itemId: item.id!, quantity })),
        contentsType
      );
      
      // Save the label to the order
      await saveLabelToOrder(order.id, labelData, {
        carrier: labelData.carrier,
        carrierName: labelData.carrier,
        trackingNumber: labelData.trackingCode,
        url: labelData.trackingUrl,
        status: 'label_created',
        estimatedDelivery: labelData.estimatedDeliveryDate ? new Date(labelData.estimatedDeliveryDate) : undefined
      });
      
      setGeneratedLabel(labelData);
      setCurrentStep('complete');
      
      toast.success('Shipping label generated successfully!');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Failed to generate label:', error);
      setError('Failed to generate shipping label. Please try again.');
      setCurrentStep('rates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewLabel = () => {
    if (generatedLabel?.labelUrl) {
      window.open(generatedLabel.labelUrl, '_blank');
    }
  };

  const handleDownloadLabel = () => {
    if (generatedLabel?.postageLabel?.label_pdf_url) {
      window.open(generatedLabel.postageLabel.label_pdf_url, '_blank');
    } else if (generatedLabel?.labelUrl) {
      window.open(generatedLabel.labelUrl, '_blank');
    }
  };

  if (!isOpen || !order) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ 
            type: "spring",
            duration: 0.5,
            bounce: 0.3 
          }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
        >
          {/* Enhanced Header */}
          <div className="relative bg-gradient-to-r from-orange-500 to-amber-500 p-6 flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-transparent"></div>
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-4">
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                  className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                >
                  <Printer className="h-6 w-6 text-white" />
                </motion.div>
                <div>
                  <motion.h2 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-2xl font-bold text-white"
                  >
                    Generate EasyPost Label
                  </motion.h2>
                  <motion.p 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-white/80 text-sm"
                  >
                    Order #{order.orderNumber || order.id.substring(0, 8)} • {formatCurrency(order.total)}
                  </motion.p>
                </div>
              </div>
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={onClose}
                  className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                >
                  <X className="h-5 w-5" />
                </Button>
              </motion.div>
            </div>
          </div>

          {/* Enhanced Content */}
          <div className="flex-1 overflow-y-auto p-8 space-y-8 bg-gradient-to-b from-gray-50/50 to-white">
            {currentStep === 'configure' && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="space-y-8"
              >
                <div className="text-left">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Configure Shipping Details</h3>
                  <p className="text-gray-600">Select your shipping configuration to generate the label</p>
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-50 border border-red-200 rounded-xl p-4"
                  >
                    <div className="flex items-center gap-2 text-red-800">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-medium">{error}</span>
                    </div>
                  </motion.div>
                )}

                {/* Ship From Address */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="space-y-3"
                >
                  <Label className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Building className="h-4 w-4 text-orange-500" />
                    Ship From Address *
                  </Label>
                  <Select value={selectedShipFromAddress} onValueChange={setSelectedShipFromAddress}>
                    <SelectTrigger className="h-12 rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 shadow-sm hover:shadow-md transition-all duration-200 text-left">
                      <SelectValue placeholder="Select ship-from address" />
                    </SelectTrigger>
                    <SelectContent>
                      {shipFromAddresses
                        .filter(address => address.id)
                        .map((address, index) => (
                        <SelectItem key={address.id || `address-${index}`} value={address.id!}>
                          <div className="flex flex-col">
                            <span className="font-medium">{address.name}</span>
                            <span className="text-sm text-gray-500">
                              {address.line1}, {address.city}, {address.state} {address.zipCode}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </motion.div>

                {/* Package Dimensions */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="space-y-3"
                >
                  <Label className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Box className="h-4 w-4 text-orange-500" />
                    Package Dimensions *
                  </Label>
                  <Select value={selectedPackageDimensions} onValueChange={setSelectedPackageDimensions}>
                    <SelectTrigger className="h-12 rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 shadow-sm hover:shadow-md transition-all duration-200 text-left">
                      <SelectValue placeholder="Select package dimensions" />
                    </SelectTrigger>
                    <SelectContent>
                      {packageDimensions
                        .filter(dimensions => dimensions.id)
                        .map((dimensions, index) => (
                        <SelectItem key={dimensions.id || `dimensions-${index}`} value={dimensions.id!}>
                          <div className="flex flex-col">
                            <span className="font-medium">{dimensions.name}</span>
                            <span className="text-sm text-gray-500">
                              {dimensions.length}" × {dimensions.width}" × {dimensions.height}"
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </motion.div>

                {/* Misc Items */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="space-y-4"
                >
                  <Label className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Tag className="h-4 w-4 text-orange-500" />
                    Additional Items (Optional)
                  </Label>
                  <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-100 space-y-4">
                    {/* Selected Items */}
                    {selectedMiscItems.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="font-medium text-gray-900">Selected Items:</h4>
                        {selectedMiscItems
                          .filter(({ item }) => item.id)
                          .map(({ item, quantity }, index) => (
                          <div key={item.id || `selected-item-${index}`} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                            <div>
                              <span className="font-medium">{item.name}</span>
                              <span className="text-sm text-gray-500 ml-2">({formatCurrency(item.value)} each)</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => updateMiscItemQuantity(item.id!, quantity - 1)}
                                className="h-8 w-8 p-0"
                              >
                                <Minus className="h-4 w-4" />
                              </Button>
                              <span className="min-w-[2rem] text-center">{quantity}</span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => updateMiscItemQuantity(item.id!, quantity + 1)}
                                className="h-8 w-8 p-0"
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => removeMiscItem(item.id!)}
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Available Items */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Available Items:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {miscItems
                          .filter(item => item.id && !selectedMiscItems.find(selected => selected.item.id === item.id))
                          .map((item, index) => (
                            <Button
                              key={item.id || `misc-item-${index}`}
                              variant="outline"
                              onClick={() => addMiscItem(item)}
                              className="justify-start text-left h-auto p-3"
                            >
                              <div className="flex flex-col items-start">
                                <span className="font-medium">{item.name}</span>
                                <span className="text-sm text-gray-500">{formatCurrency(item.value)}</span>
                              </div>
                            </Button>
                          ))
                        }
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Contents Type */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="space-y-3"
                >
                  <Label className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Globe className="h-4 w-4 text-orange-500" />
                    Contents Type *
                  </Label>
                  <Select value={contentsType} onValueChange={(value: any) => setContentsType(value)}>
                    <SelectTrigger className="h-12 rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 shadow-sm hover:shadow-md transition-all duration-200 text-left">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="merchandise">Merchandise</SelectItem>
                      <SelectItem value="gift">Gift</SelectItem>
                      <SelectItem value="documents">Documents</SelectItem>
                      <SelectItem value="sample">Sample</SelectItem>
                      <SelectItem value="returned_goods">Returned Goods</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </motion.div>
              </motion.div>
            )}

            {currentStep === 'rates' && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="space-y-8"
              >
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Shipping Rate</h3>
                  <p className="text-gray-600">Choose your preferred shipping option</p>
                </div>

                {availableRates.length > 0 ? (
                  <div className="space-y-4">
                    {availableRates.map((rate, index) => (
                      <motion.div
                        key={rate.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 + index * 0.1 }}
                        className={`cursor-pointer transition-all rounded-xl border-2 p-6 ${
                          selectedRate?.id === rate.id
                            ? 'ring-2 ring-orange-500/50 border-orange-500 bg-gradient-to-br from-orange-50 to-amber-50'
                            : 'border-gray-200 hover:border-orange-300 hover:shadow-md bg-white'
                        }`}
                        onClick={() => setSelectedRate(rate)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-gray-900">{rate.carrier} - {rate.service}</h4>
                            <p className="text-sm text-gray-600">
                              {rate.delivery_date ? `Delivery: ${new Date(rate.delivery_date).toLocaleDateString()}` : 
                               rate.delivery_days ? `${rate.delivery_days} business days` : 
                               'Standard delivery'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-orange-600">
                              {formatCurrency(parseFloat(rate.rate))}
                            </p>
                            <p className="text-sm text-gray-500">{rate.currency}</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No shipping rates available</p>
                  </div>
                )}
              </motion.div>
            )}

            {currentStep === 'generate' && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="flex flex-col items-center justify-center py-12"
              >
                <Loader2 className="h-12 w-12 text-orange-500 animate-spin mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Generating Label</h3>
                <p className="text-gray-600">Please wait while we create your shipping label...</p>
              </motion.div>
            )}

            {currentStep === 'complete' && generatedLabel && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="space-y-6"
              >
                <div className="text-center mb-6">
                  <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Label Generated Successfully!</h3>
                  <p className="text-gray-600">Your shipping label is ready for download and printing</p>
                </div>

                <Card>
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">Carrier:</span>
                        <Badge className="bg-jamaica-green text-white">{generatedLabel.carrier}</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">Service:</span>
                        <span className="text-gray-600">{generatedLabel.service}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">Tracking Number:</span>
                        <span className="font-mono text-gray-600">{generatedLabel.trackingCode}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">Cost:</span>
                        <span className="text-gray-600">{formatCurrency(generatedLabel.rate)} {generatedLabel.currency}</span>
                      </div>
                      {generatedLabel.estimatedDeliveryDate && (
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">Estimated Delivery:</span>
                          <span className="text-gray-600">
                            {new Date(generatedLabel.estimatedDeliveryDate).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>

                    <Separator className="my-4" />

                    <div className="flex gap-3">
                      <Button
                        onClick={handleViewLabel}
                        variant="outline"
                        className="flex-1"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Label
                      </Button>
                      <Button
                        onClick={handleDownloadLabel}
                        className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white flex-1"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>

          {/* Enhanced Footer */}
          <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                {currentStep === 'configure' && 'Step 1 of 3: Configuration'}
                {currentStep === 'rates' && 'Step 2 of 3: Rate Selection'}
                {currentStep === 'generate' && 'Step 3 of 3: Generating Label'}
                {currentStep === 'complete' && 'Complete'}
              </div>
              
              <div className="flex items-center gap-4">
                {currentStep !== 'complete' && currentStep !== 'generate' && (
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button 
                      variant="outline" 
                      onClick={onClose}
                      disabled={isLoading}
                      className="px-6 h-11 rounded-xl font-medium"
                    >
                      Cancel
                    </Button>
                  </motion.div>
                )}

                {currentStep === 'configure' && (
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button 
                      onClick={validateConfiguration}
                      disabled={isLoading || !selectedShipFromAddress || !selectedPackageDimensions}
                      className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-orange-500/25"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Validating...
                        </>
                      ) : (
                        'Continue'
                      )}
                    </Button>
                  </motion.div>
                )}

                {currentStep === 'rates' && (
                  <>
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button 
                        variant="outline" 
                        onClick={() => setCurrentStep('configure')}
                        disabled={isLoading}
                        className="px-6 h-11 rounded-xl font-medium"
                      >
                        Back
                      </Button>
                    </motion.div>
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button 
                        onClick={generateLabel}
                        disabled={isLoading || !selectedRate}
                        className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-orange-500/25"
                      >
                        Generate Label - {selectedRate ? formatCurrency(parseFloat(selectedRate.rate)) : ''}
                      </Button>
                    </motion.div>
                  </>
                )}

                {currentStep === 'complete' && (
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button 
                      onClick={onClose}
                      className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-orange-500/25"
                    >
                      Done
                    </Button>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}